import { useState, useEffect, useRef } from "react";
import { Box, Button } from "@mui/material";
import { SingleSelectDropdown, MultiSelectDropdown } from "../FilterDropdowns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import DatePickerField from "./DatePickerField";
import { FinancialExportStyles } from "./styles";
import { useFinanceExportData } from "./useFinanceExportData";
import { HandleGenerateExportProps } from "./type";

const FilterSection = ({
  handleGenerateExport,
  dashboardId,
}: HandleGenerateExportProps) => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(6, "month"));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const hasInitializedDefault = useRef(false);

  const {
    typeOptions,
    typeOptionsLoading,
    companyOptions,
    companyOptionsLoading,
    sourceOptions,
    sourceOptionsLoading,
  } = useFinanceExportData(dashboardId);

  useEffect(() => {
    if (typeOptions.length > 0 && !hasInitializedDefault.current) {
      const invoiceOption = typeOptions.find(
        (option) => option.value.toLowerCase() === "invoice"
      );
      if (invoiceOption) {
        setSelectedType(invoiceOption.value);
        hasInitializedDefault.current = true;
      }
    }
  }, [typeOptions]);

  const isBillsType = selectedType?.toLowerCase() === "bill";
  const showCompanyFilter =
    selectedType?.toLowerCase() === "bill" ||
    selectedType?.toLowerCase() === "invoice";

  useEffect(() => {
    if (!showCompanyFilter && selectedCompanies.length > 0) {
      setSelectedCompanies([]);
    }
  }, [showCompanyFilter, selectedCompanies.length]);

  useEffect(() => {
    if (!isBillsType && selectedSource !== null) {
      setSelectedSource(null);
    }
  }, [isBillsType, selectedCompanies.length, selectedSource]);

  useEffect(() => {
    if (startDate && !endDate) {
      setEndDate(dayjs());
    }
  }, [startDate, endDate]);

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
  };

  const handleStartDateAccept = (newValue: Dayjs | null) => {
    setStartDate(newValue);
    setIsStartDatePickerOpen(false);
  };

  const handleEndDateAccept = (newValue: Dayjs | null) => {
    setEndDate(newValue);
    if (newValue && !startDate) {
      const sixMonthsAgo = dayjs().subtract(6, "month");
      const defaultStartDate = newValue.subtract(1, "month");
      const finalStartDate = defaultStartDate.isBefore(sixMonthsAgo)
        ? sixMonthsAgo
        : defaultStartDate;
      setStartDate(finalStartDate);
    }
    setIsEndDatePickerOpen(false);
  };

  const handleGenerate = async () => {
    if (!selectedType) {
      return;
    }
    try {
      const requestData = {
        export_data_type: selectedType,
        from_date: startDate?.format("YYYY-MM-DD"),
        to_date: endDate?.format("YYYY-MM-DD"),
        dashboard_id: dashboardId,
        companies: showCompanyFilter ? selectedCompanies : [],
        source: isBillsType ? selectedSource : null,
      };

      await handleGenerateExport(requestData);

      setSelectedType(null);
      setSelectedCompanies([]);
      setSelectedSource(null);
    } catch (error) {
      console.error("Error generating export:", error);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={FinancialExportStyles.filterBoxes}>
        <Box sx={FinancialExportStyles.filterGrid}>
          <Box sx={FinancialExportStyles.filterInputWidth}>
            <SingleSelectDropdown
              placeholder={`${typeOptions.length} Options`}
              onSelect={setSelectedType}
              selectedValue={selectedType}
              loading={typeOptionsLoading}
              disabled={false}
              label="Type*"
              options={typeOptions}
              labelMarginBottom="0"
            />
          </Box>

          {showCompanyFilter && (
            <Box sx={FinancialExportStyles.filterInputWidth}>
              <MultiSelectDropdown
                placeholder={`${companyOptions.length} Options`}
                onSelect={setSelectedCompanies}
                selectedValues={selectedCompanies}
                loading={companyOptionsLoading}
                label="Company"
                options={companyOptions}
                labelMarginBottom="0.25rem"
                marginBottom="0"
              />
            </Box>
          )}

          {isBillsType && (
            <Box sx={FinancialExportStyles.filterInputWidth}>
              <SingleSelectDropdown
                placeholder={`${sourceOptions.length} Options`}
                onSelect={setSelectedSource}
                selectedValue={selectedSource}
                loading={sourceOptionsLoading}
                disabled={false}
                label="Source"
                options={sourceOptions}
                labelMarginBottom="0.25rem"
              />
            </Box>
          )}

          <DatePickerField
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            onAccept={handleStartDateAccept}
            isOpen={isStartDatePickerOpen}
            setIsOpen={setIsStartDatePickerOpen}
            ariaLabel="Start Date"
            maxDate={endDate}
          />

          <DatePickerField
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            onAccept={handleEndDateAccept}
            isOpen={isEndDatePickerOpen}
            setIsOpen={setIsEndDatePickerOpen}
            ariaLabel="End Date"
            minDate={startDate}
          />
        </Box>
        <Box sx={FinancialExportStyles.filterButtonBox}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerate}
            disabled={!selectedType}
            sx={FinancialExportStyles.filterButton}
          >
            GENERATE
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default FilterSection;
